import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, X, Mail, ArrowRight } from 'lucide-react';
import { useNotification } from '../contexts/NotificationContext';
import { useSubscription } from '../hooks/useSubscription';
import { getApiUrl } from '../config/api';
import axios from 'axios';
import EmailConfirmation from './setup/EmailConfirmation';

const SetupWizard = ({ onComplete, isModal = false, isFirstTimeImport = false }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { getMaxFamilyMembers } = useSubscription();
  
  const [step, setStep] = useState(isFirstTimeImport ? 2 : 1); // 1: Welcome/Question, 2: Email Confirmation
  const [selectedOption, setSelectedOption] = useState(null); // 'yes' or 'no'

  const maxFamilyMembers = getMaxFamilyMembers();

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    if (option === 'yes') {
      setStep(2);
    } else {
      // User chose not to import - complete wizard
      if (onComplete) {
        onComplete();
      } else {
        navigate('/dashboard');
      }
      showNotification('Setup completed successfully!', 'success');
    }
  };

  const handleEmailConfirmationComplete = (stepName, data) => {
    // After email confirmation, trigger initial sync and complete wizard
    // Pass through sync results if available
    if (onComplete) {
      onComplete(stepName, data);
    } else {
      navigate('/dashboard');
    }
    if (!data?.syncResults) {
      showNotification('Setup completed successfully!', 'success');
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    } else {
      navigate('/dashboard');
    }
    showNotification('Setup completed successfully!', 'success');
  };

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-semibold">Welcome to MyTrailer</h2>
            <button
              onClick={onComplete}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-6">
            {step === 1 && (
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Set Up Your Data</h3>
                <p className="text-gray-600 mb-8">
                  Do you want to check for KCI entries, results, and memberships? We'll help you set up automatic syncing.
                </p>
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => handleOptionSelect('yes')}
                    className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Yes, Import My Data
                  </button>
                  <button
                    onClick={() => handleOptionSelect('no')}
                    className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Skip for Now
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <EmailConfirmation
                onComplete={handleEmailConfirmationComplete}
                onSkip={handleComplete}
                wizardData={{ isFirstTimeImport }}
                isCompleted={false}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-2xl w-full mx-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {step === 1 && (
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Welcome to MyTrailer</h1>
              <p className="text-gray-600 mb-8">
                Do you want to import your data from KCIRodeo associations? This includes entries, results, memberships, and family members.
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => handleOptionSelect('yes')}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Yes, Import My Data
                </button>
                <button
                  onClick={() => handleOptionSelect('no')}
                  className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Skip for Now
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <EmailConfirmation
              onComplete={handleEmailConfirmationComplete}
              onSkip={handleComplete}
              wizardData={{ isFirstTimeImport }}
              isCompleted={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
